import React, { useEffect, useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Button,
  Alert
} from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import api from '../services/authService';
import { saveSignupProgress, getSignupProgress, SignupStep } from '../utils/signupProgress';

function VerifyEmail() {
  const [status, setStatus] = useState('pending');
  const [error, setError] = useState('');
  const [userRole, setUserRole] = useState(null);
  const [verifiedEmail, setVerifiedEmail] = useState('');
  const { token } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const email = location.state?.email;

  useEffect(() => {
    if (token) {
      verifyEmail(token);
    }
  }, [token]);

  const verifyEmail = async (verificationToken) => {
    try {
      const response = await api.post('/auth/verify-email', {
        token: verificationToken
      });

      setStatus('success');
      setUserRole(response.data.role);
      setVerifiedEmail(response.data.email || email);

      // Only redirect to login if not an affiliate (affiliates should continue signup)
      if (response.data.role !== 'affiliate') {
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        // For affiliates, get saved form data and redirect to terms page
        const savedProgress = getSignupProgress();
        const formData = {
          ...(savedProgress?.data?.formData || {}),
          name: response.data.name,
          username: response.data.username,
          profileIcon: response.data.profileIcon
        };
        const userEmail = response.data.email || email || savedProgress?.data?.email;

        // Save progress to terms step
        saveSignupProgress(SignupStep.TERMS, {
          email: userEmail,
          formData: formData,
          emailVerified: true // Flag to show verification message
        });

        // Redirect to terms page with verification message
        setTimeout(() => {
          navigate('/terms-agreement', {
            state: {
              email: userEmail,
              formData: formData,
              emailVerified: true
            }
          });
        }, 1500);
      }
    } catch (err) {
      setStatus('error');
      const errorMessage = err.response?.data?.message || err.message || 'Verification failed';
      setError(errorMessage);
    }
  };

  const resendVerification = async () => {
    if (!email) {
      setError('Email address is required to resend verification');
      return;
    }

    try {
      await api.post('/auth/resend-verification', { email });
      setStatus('resent');
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to resend verification email';
      setError(errorMessage);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 4 }}>
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <img
            src="/log_big.png"
            alt="Boost Health Daily"
            style={{ maxWidth: '120px', height: 'auto' }}
          />
        </Box>
        {!token ? (
          <>
            <Typography variant="h5" gutterBottom>
              Verify Your Email
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              We've sent a verification email to <strong>{email}</strong>.
              Please check your inbox and click the verification link.
            </Typography>
            <Button
              variant="outlined"
              onClick={resendVerification}
              disabled={status === 'resent'}
            >
              Resend Verification Email
            </Button>
            {status === 'resent' && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Verification email has been resent!
              </Alert>
            )}
          </>
        ) : (
          <>
            {status === 'pending' && (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress />
              </Box>
            )}
            {status === 'success' && (
              <>
                <Typography variant="h5" gutterBottom sx={{ color: 'success.main' }}>
                  Email Verified Successfully!
                </Typography>
                {userRole === 'affiliate' ? (
                  <Typography variant="body1" sx={{ mb: 3 }}>
                    Your email has been verified. Please continue with your signup process.
                  </Typography>
                ) : (
                  <Typography variant="body1">
                    Redirecting to login page...
                  </Typography>
                )}
              </>
            )}
            {status === 'error' && (
              <>
                <Typography variant="h5" gutterBottom color="error">
                  Verification Failed
                </Typography>
                <Typography variant="body1" color="error" sx={{ mb: 2 }}>
                  {error}
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => navigate('/signup')}
                  sx={{ mt: 2 }}
                >
                  Back to Sign Up
                </Button>
              </>
            )}
          </>
        )}
      </Paper>
    </Container>
  );
}

export default VerifyEmail;

